import React from 'react';
import { Plus, X, Check } from 'lucide-react';
import { SONDAGE, SONDAGE_OPTIONS_ECRAN, DUREES_SONDAGE, basculerBonne, retirerOption } from '../../lib/canalPosts';

// 17/09 (Marlon) : champs d'un SONDAGE du canal (question, options, choix multiples, quiz + bonne réponse + explication,
// fermeture automatique). Limites = doc Bot API sendPoll (voir lib/canalPosts.js). Composant contrôlé : value / onChange.
const champ = 'block mt-1 w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600';
const compteur = (n, max) => <span className={`normal-case tracking-normal text-[10px] ${n > max ? 'text-rose-400' : 'text-slate-600'}`}>{n} / {max}</span>;

function Interrupteur({ actif, onClick, testid, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={actif}
      className={`flex items-center gap-2 px-3 h-8 rounded-lg border text-xs transition-colors ${actif ? 'border-indigo-400/60 bg-indigo-400/15 text-white' : 'border-white/10 bg-white/5 text-slate-400 hover:text-white'}`}>
      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${actif ? 'border-indigo-300 bg-indigo-400/40' : 'border-slate-500'}`}>{actif && <Check size={10} />}</span>
      {children}
    </button>
  );
}

export default function CanalSondage({ value, onChange }) {
  const s = value;
  const maj = (champs) => onChange({ ...s, ...champs });
  const setOption = (i, texte) => maj({ options: s.options.map((o, k) => (k === i ? texte : o)) });
  return (
    <div className="space-y-3" data-testid="canal-sondage">
      <label className="block text-[11px] uppercase tracking-wider text-slate-500">Question
        <textarea data-testid="sondage-question" value={s.question} onChange={e => maj({ question: e.target.value })} rows={2} placeholder="Tu préfères quoi ce soir ?" className={`${champ} resize-none`} />
        <span className="block text-right">{compteur(s.question.trim().length, SONDAGE.question)}</span>
      </label>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
          <span>Options {s.quiz && <span className="normal-case tracking-normal text-slate-400">— coche la bonne réponse{s.multiple ? ' (une ou plusieurs)' : ''}</span>}</span>
          <span className="normal-case tracking-normal text-[10px] text-slate-600">{s.options.length} / {SONDAGE.optionsMax}</span>
        </div>
        <div className="mt-1 space-y-1.5">
          {s.options.map((o, i) => {
            const bonne = (s.bonnes || []).includes(i);
            return (
              <div key={i} className="flex items-center gap-1.5" data-testid={`sondage-option-ligne-${i}`}>
                {s.quiz && (
                  <button type="button" onClick={() => onChange(basculerBonne(s, i))} data-testid={`sondage-bonne-${i}`} aria-pressed={bonne} title="Bonne réponse"
                    className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center ${bonne ? 'border-emerald-400 bg-emerald-500/25 text-emerald-200' : 'border-white/15 text-slate-600 hover:text-white'}`}><Check size={13} /></button>
                )}
                <input data-testid={`sondage-option-${i}`} value={o} onChange={e => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`}
                  className="flex-1 min-w-0 bg-[#18171C] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none placeholder:text-slate-600" />
                <span className={`w-12 shrink-0 text-right text-[10px] ${o.trim().length > SONDAGE.option ? 'text-rose-400' : 'text-slate-600'}`}>{o.trim().length}/{SONDAGE.option}</span>
                <button type="button" onClick={() => onChange(retirerOption(s, i))} disabled={s.options.length <= SONDAGE_OPTIONS_ECRAN} data-testid={`sondage-retirer-${i}`} title="Retirer l'option"
                  className="w-7 h-7 shrink-0 rounded-md text-slate-500 hover:text-rose-300 disabled:opacity-30 disabled:hover:text-slate-500 flex items-center justify-center"><X size={14} /></button>
              </div>
            );
          })}
        </div>
        {s.options.length < SONDAGE.optionsMax && (
          <button type="button" onClick={() => maj({ options: [...s.options, ''] })} data-testid="sondage-ajouter" className="mt-1.5 text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"><Plus size={13} /> Ajouter une option</button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Interrupteur actif={s.multiple} testid="sondage-multiple" onClick={() => maj({ multiple: !s.multiple, bonnes: !s.multiple ? s.bonnes : (s.bonnes || []).slice(0, 1) })}>Choix multiples</Interrupteur>
        <Interrupteur actif={s.quiz} testid="sondage-quiz" onClick={() => maj({ quiz: !s.quiz })}>Quiz (bonne réponse)</Interrupteur>
        <label className="flex items-center gap-2 text-xs text-slate-400">Fermeture auto
          <select data-testid="sondage-duree" value={s.duree_s || 0} onChange={e => maj({ duree_s: Number(e.target.value) })} className="h-8 bg-[#18171C] border border-white/10 rounded-lg px-2 text-xs text-white outline-none">
            {DUREES_SONDAGE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      {s.quiz && (
        <label className="block text-[11px] uppercase tracking-wider text-slate-500">Explication (facultatif)
          <textarea data-testid="sondage-explication" value={s.explication} onChange={e => maj({ explication: e.target.value })} rows={2} placeholder="Affichée après une mauvaise réponse 💡" className={`${champ} resize-none`} />
          <span className="block text-right">{compteur(s.explication.trim().length, SONDAGE.explication)}</span>
        </label>
      )}
      <div className="text-[11px] text-slate-500">Sondage anonyme (réglage par défaut de Telegram).</div>
    </div>
  );
}
